import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { TableComponent } from 'components/table/TableComponent'
import { RolodexDocumentEntry } from 'features/files/types'
import { type FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { DocumentPreview } from './DocumentPreview'
import {
  createGenericFileTableColumns,
  GenericFileTableColumnsOptions,
} from './GenericFileTableColumns'

const PageContainer = styled.div`
  width: 100%;
  overflow: auto;
`

const PreviewContainer = styled.div`
  margin-top: 1rem;
`

export interface GenericFileTableProps {
  documents: ReadonlyArray<RolodexDocumentEntry>
  isLoading: boolean
  forceRefresh?: () => Promise<void> | void
  customColumns?: ReadonlyArray<ColumnDef<RolodexDocumentEntry>>
  showColumnConfig?: boolean
  showFilterBar?: boolean
  columnOptions?: GenericFileTableColumnsOptions
  documentFilter?: (document: RolodexDocumentEntry) => boolean
  showPreview?: boolean
}

export const GenericFileTable: FC<GenericFileTableProps> = ({
  documents,
  isLoading,
  forceRefresh,
  customColumns,
  showColumnConfig = true,
  showFilterBar = true,
  columnOptions = {},
  documentFilter,
  showPreview = true,
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 24,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'upload_date', desc: true },
  ])
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null)

  const filteredDocuments = useMemo(() => {
    return documentFilter ? documents.filter(documentFilter) : documents
  }, [documentFilter, documents])

  const { showDownload, showDelete } = columnOptions

  const columns = useMemo(
    () =>
      customColumns
        ? [...customColumns]
        : [
            ...createGenericFileTableColumns({
              showDownload,
              showDelete,
              forceRefresh,
            }),
          ],
    [showDownload, showDelete, customColumns, forceRefresh]
  )

  const globalFilterFn: FilterFn<RolodexDocumentEntry> = useCallback(
    (row, _, filterValue) => {
      const searchValue =
        typeof filterValue === 'string'
          ? filterValue.toLowerCase()
          : String(filterValue || '').toLowerCase()

      const document = row.original
      return [
        document.visible_name,
        document.filename,
        document.file_type,
        ...document.categories,
      ]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(searchValue))
    },
    []
  )

  const selectedDocument = useMemo(() => {
    if (!selectedFileId) return null
    return (
      filteredDocuments.find(
        (document) => document.entry_id === selectedFileId
      ) ?? null
    )
  }, [filteredDocuments, selectedFileId])

  return (
    <PageContainer>
      <TableComponent
        isLoadingData={isLoading}
        data={[...filteredDocuments]}
        columns={columns}
        itemDescriptiveName='Document(s)'
        pagination={pagination}
        globalFilterFn={globalFilterFn}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={(row) => setSelectedFileId(row.original.entry_id)}
        showColumnConfig={showColumnConfig}
        showFilterBar={showFilterBar}
        controlledHighlightedRowId={selectedFileId}
        getRowId={(row) => row.entry_id}
      />

      {showPreview && selectedDocument && (
        <PreviewContainer>
          <DocumentPreview document={selectedDocument} />
        </PreviewContainer>
      )}
    </PageContainer>
  )
}
